import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validaIscrizione } from '@corsi/modulo.ts';
import { CampiIscrizione, portaAlPrimoErrore } from '../components/iscrizione/CampiIscrizione';
import { ConfermaIscrizione } from '../components/iscrizione/ConfermaIscrizione';
import { CornicePubblica } from '../components/iscrizione/CornicePubblica';
import { FondoModulo } from '../components/iscrizione/FondoModulo';
import { useModuloIscrizione } from '../components/iscrizione/useModuloIscrizione';
import { BRANDING } from '../lib/branding';
import { oggiRoma } from '../lib/dataItalia';

/**
 * Modulo pubblico di iscrizione ai corsi: si apre senza login, da un link o
 * da una pagina del sito. Chi lo compila finisce nei Lead, nei Clienti (con il
 * badge Corsista) e nei contatti del Mailing: lo fa la funzione
 * iscrizione-corsi.
 *
 * Il link puo' portare il nome del corso: /iscrizione-corsi?corso=Aggiornamento%20RSPP
 * Il nome compare in cima alla pagina e sul lead, come "campagna".
 *
 * Campi e sezioni ricalcano il modulo di registrazione di Safety Expo; la
 * grafica e' quella di Pixel (vedi CornicePubblica). Le regole dei campi
 * stanno in @corsi/modulo.ts, le stesse che usa la funzione: qui servono a
 * dire subito cosa manca, senza aspettare il server.
 */

const FUNZIONE = `${String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '')}/functions/v1/iscrizione-corsi`;
const LUNGHEZZA_MASSIMA_CORSO = 150;

type Fase = 'compilazione' | 'invio' | 'fatta';

export default function IscrizioneCorsiPage() {
  const [parametri] = useSearchParams();
  const corso = (parametri.get('corso') ?? '').trim().slice(0, LUNGHEZZA_MASSIMA_CORSO);

  const { modulo, errori, setErrori, aggiorna, reimposta, trappola, setTrappola } = useModuloIscrizione();
  const [fase, setFase] = useState<Fase>('compilazione');
  const [erroreInvio, setErroreInvio] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Iscrizione ai corsi — ${BRANDING.nomeBreve}`;
  }, []);

  const oggi = oggiRoma();

  async function invia(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setErroreInvio(null);

    const controllo = validaIscrizione(modulo, oggi, corso);
    if (!controllo.ok) {
      setErrori(controllo.errori);
      setErroreInvio('Controlla i campi segnalati in rosso.');
      portaAlPrimoErrore(controllo.errori);
      return;
    }

    setFase('invio');
    try {
      const risposta = await fetch(FUNZIONE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modulo, corso: corso || null, sito: trappola, pagina: window.location.href }),
      });
      const corpo = await risposta.json().catch(() => ({}));

      if (risposta.ok && corpo.ok) {
        setFase('fatta');
        window.scrollTo({ top: 0 });
        return;
      }
      if (corpo.errori) {
        setErrori(corpo.errori);
        portaAlPrimoErrore(corpo.errori);
        setErroreInvio('Controlla i campi segnalati in rosso.');
      } else {
        setErroreInvio(corpo.error ?? 'Iscrizione non riuscita. Riprova fra qualche minuto.');
      }
    } catch {
      setErroreInvio('Connessione non riuscita. Controlla la rete e riprova.');
    }
    setFase('compilazione');
  }

  function nuovaIscrizione(): void {
    reimposta();
    setErroreInvio(null);
    setFase('compilazione');
    window.scrollTo({ top: 0 });
  }

  return (
    <CornicePubblica>
      <h1 className="text-center text-4xl font-bold tracking-wide text-white drop-shadow-lg sm:text-5xl">REGISTRAZIONE</h1>
      <p className="mt-2 text-center text-base uppercase tracking-[0.2em] text-gray-300">Corsi di formazione</p>

      {fase === 'fatta' ? (
        <ConfermaIscrizione nome={modulo.nome} corso={corso} conConsenso={modulo.consensoInformazioni === 'si'} onAltra={nuovaIscrizione} />
      ) : (
        <>
          <div className="glass-morphism-card mt-6 rounded-2xl px-5 py-4 text-lg leading-relaxed text-white">
            {corso ? (
              <>
                Iscrizione al corso <strong className="text-primary-300">{corso}</strong>. Compila il modulo: ti ricontatteremo
                con date, sede e modalità.
              </>
            ) : (
              <>
                Iscriviti ai <strong className="text-primary-300">corsi di formazione</strong> su salute e sicurezza sul lavoro.
                Compila il modulo: ti ricontatteremo con tutti i dettagli.
              </>
            )}
          </div>

          <form onSubmit={invia} noValidate className="foglio-quadretti mt-6 rounded-2xl px-4 py-6 shadow-2xl ring-1 ring-black/5 sm:px-8 sm:py-8">
            <CampiIscrizione modulo={modulo} errori={errori} oggi={oggi} aggiorna={aggiorna} />
            <FondoModulo erroreInvio={erroreInvio} inviando={fase === 'invio'} testo="Registrati" trappola={trappola} onTrappola={setTrappola} />
          </form>
        </>
      )}
    </CornicePubblica>
  );
}
